import { filter, includes, map, range } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AccessLevels, AccessLevel } from "@/shared/constants";
import { DropdownMenu, DropdownMenuProps } from "@/shared/widgets/DropdownMenu";
import { SelectButton } from "@/shared/ui/SelectButton";

interface AccessLevelSelectDropdownProps
  extends Omit<
    DropdownMenuProps,
    "trigger" | "items" | "onSelect" | "children"
  > {
  value?: AccessLevel;
  placeholder?: string;
  onSelect: (access_level: AccessLevel) => void | Promise<boolean | void>;
  filter?: AccessLevel[];
  className?: string;
}

function AccessLevelSelectDropdown(props: AccessLevelSelectDropdownProps) {
  const {
    value: externalValue,
    placeholder,
    filter: filterLevels = [],
    onSelect,
    className,
    ...rest
  } = props;
  const { t } = useTranslation();
  const [internalValue, setInternalValue] = useState<AccessLevel | undefined>(
    externalValue
  );

  const getAccessLevelLabel = (level?: AccessLevel) => {
    if (!level) return undefined;
    switch (level) {
      case AccessLevel.none:
        return t("common.access_level_none");
      case AccessLevel.read:
        return t("common.access_level_read");
      case AccessLevel.limited:
        return t("common.access_level_limited");
      case AccessLevel.edit:
        return t("common.access_level_edit");
      case AccessLevel.full:
        return t("common.access_level_full");
      default:
        return undefined;
    }
  };

  useEffect(() => {
    setInternalValue(externalValue);
  }, [externalValue]);

  const handleSelect = async (roleKey: AccessLevel) => {
    const previousValue = internalValue;
    setInternalValue(roleKey);

    const result = await onSelect(roleKey);

    // If onSelect returns false, revert to previous value
    if (result === false) {
      setInternalValue(previousValue);
    }
  };

  const avaliableAccessLevels = filter(AccessLevels, (level) => {
    if (filterLevels?.length) {
      return includes(filterLevels, level.accesslevel);
    }
    return true;
  });

  const options = map(avaliableAccessLevels, (accessLevel) => ({
    label:
      getAccessLevelLabel(accessLevel.key as AccessLevel) || accessLevel.name,
    value: accessLevel.key,
    onClick: () => handleSelect(accessLevel.key as AccessLevel),
    selected: accessLevel.key === internalValue,
  }));

  return (
    <DropdownMenu
      {...rest}
      items={options}
      separatorIndexes={range(1, options.length)}
      matchTriggerWidth
      trigger={
        <SelectButton
          size="sm"
          value={getAccessLevelLabel(internalValue)}
          placeholder={placeholder || t("common.select")}
          className={className}
        />
      }
      side="bottom"
      align="center"
    />
  );
}

export { AccessLevelSelectDropdown };
